/**
 * VeraLok Admin Console — internal mission control (Iteration 21C-C).
 *
 * NOT a customer-facing surface. Every page requires an INTERNAL_ROLES
 * session. Existing operator pages are re-used as content wherever they
 * already implement the required functionality; new pages are created
 * only for the sections that didn't previously have one.
 */
import React from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { InternalOnlyRoute } from "./PortalGuards";
import VeraLokLogo from "@/marketing/VeraLokLogo";
import { PortalHeaderUserMenu } from "./PortalHeaderUserMenu";
import "@/marketing/design-system.css";

// Re-use existing pages as content where they already exist.
import AdminControlPlane from "./pages/AdminControlPlane";
import InstitutionsPage from "@/pages/Institutions";
import InstitutionDetail from "@/pages/InstitutionDetail";
import ApprovalQueue from "@/pages/ApprovalQueue";
import Identities from "@/pages/Identities";
import IdentityDetail from "@/pages/IdentityDetail";
import Recovery from "@/pages/Recovery";
import RecoveryDetail from "@/pages/RecoveryDetail";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import LiveAudit from "@/pages/LiveAudit";
import MfaSecurity from "@/pages/Security";
import Analytics from "@/pages/Analytics";
import SettingsPage from "@/pages/Settings";
import EmailOps from "@/pages/EmailOps";
import Digest from "@/pages/Digest";
import CapReviews from "./pages/CapReviews";
import Suspensions from "./pages/Suspensions";
import PlatformHealth from "./pages/PlatformHealth";
import ProviderConfig from "./pages/ProviderConfig";
import CommercialDashboard from "./pages/CommercialDashboard";
import WalletPortraits from "./pages/WalletPortraits";
import EnrolmentSecurity from "./pages/EnrolmentSecurity";
import AdminKycReviews from "./pages/AdminKycReviews";

// Sidebar sections grouped for scan-ability. M3 (Surface Ownership):
// existing top-level Admin capabilities (Approval Queue, Identities,
// Recovery, Users) surfaced inside the Admin Console shell rather than
// as orphaned top-level routes. Backend permissions are unchanged —
// InternalOnlyRoute already restricts the shell to internal roles.
const SECTIONS = [
  { key: "dashboard", label: "Dashboard", path: "" },
  // Institutional oversight
  { key: "institutions", label: "Institutions", path: "institutions" },
  { key: "approvals", label: "Approval Queue", path: "approvals" },
  { key: "cap", label: "CAP Reviews", path: "cap" },
  { key: "kyc-reviews", label: "KYC Reviews", path: "kyc-reviews" },
  { key: "suspensions", label: "Suspensions", path: "suspensions" },
  // Citizen identity lifecycle
  { key: "identities", label: "Identities", path: "identities" },
  { key: "recovery", label: "Recovery Requests", path: "recovery" },
  // Audit & analytics
  { key: "global-audit", label: "Global Audit", path: "audit" },
  { key: "global-analytics", label: "Global Analytics", path: "analytics" },
  { key: "platform-health", label: "Platform Health", path: "health" },
  // Commercial
  { key: "commercial", label: "Commercial", path: "commercial" },
  // Platform administration
  { key: "users", label: "Users", path: "users" },
  { key: "mfa-security", label: "MFA & Account Security", path: "mfa-security" },
  { key: "provider-config", label: "Provider Configuration", path: "providers" },
  { key: "system-settings", label: "System Settings", path: "settings" },
  { key: "wallet-portraits", label: "Wallet Portraits", path: "wallet-portraits" },
  { key: "enrolment-security", label: "Enrolment Security", path: "enrolment-security" },
];

export default function AdminShell() {
  return (
    <InternalOnlyRoute>
      <div className="vlk-portal vlk-portal--admin min-h-screen flex" data-testid="admin-shell">
        <AdminSidebar />
        <main className="flex-1 min-w-0">
          <AdminHeaderStrip />
          <div className="p-6">
            <Routes>
              <Route path="" element={<AdminControlPlane />} />
              <Route path="institutions" element={<InstitutionsPage />} />
              <Route path="institutions/:vlk_inst_id" element={<InstitutionDetail />} />
              <Route path="approvals" element={<ApprovalQueue />} />
              <Route path="cap" element={<CapReviews />} />
              <Route path="kyc-reviews" element={<AdminKycReviews />} />
              <Route path="suspensions" element={<Suspensions />} />
              <Route path="identities" element={<Identities />} />
              <Route path="identities/:vlk_id" element={<IdentityDetail />} />
              <Route path="recovery" element={<Recovery />} />
              <Route path="recovery/:request_id" element={<RecoveryDetail />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="audit/live" element={<LiveAudit />} />
              <Route path="mfa-security" element={<MfaSecurity />} />
              <Route path="health" element={<PlatformHealth />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="commercial" element={<CommercialDashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="providers" element={<ProviderConfig />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="wallet-portraits" element={<WalletPortraits />} />
              <Route path="enrolment-security" element={<EnrolmentSecurity />} />
              <Route path="email" element={<EmailOps />} />
              <Route path="digest" element={<Digest />} />
              {/* Backwards-compat: old `security` slug redirected to the renamed MFA settings surface. */}
              <Route path="security" element={<Navigate to="../mfa-security" replace />} />
              <Route path="*" element={<Navigate to="" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </InternalOnlyRoute>
  );
}

function AdminHeaderStrip() {
  const { user } = useAuth();
  return (
    <div className="vlk-portal-header vlk-portal-header--admin px-6 py-3 flex items-center justify-between text-xs" data-testid="admin-header-strip">
      <div className="text-white/60 flex items-center gap-3">
        <VeraLokLogo size={22} showWordmark={false} />
        <span className="font-semibold text-white/80">Admin Console</span>
        <span className="text-white/30">·</span>
        <span className="text-white/50 hidden md:inline" data-testid="admin-header-descriptor">Approvals · Identities · Compliance · Audit</span>
        <span className="text-white/30 hidden md:inline">·</span>
        <span className="vlk-portal-role-pill vlk-portal-role-pill--internal" data-testid="admin-internal-badge">Internal Only</span>
      </div>
      <div className="text-white/70 flex items-center gap-3">
        <PortalHeaderUserMenu variant="admin" testIdPrefix="admin" />
      </div>
    </div>
  );
}

function AdminSidebar() {
  return (
    <aside className="vlk-portal-sidebar w-60 p-4 space-y-1 shrink-0" data-testid="admin-sidebar">
      <div className="mb-4 flex items-center gap-2">
        <VeraLokLogo size={24} />
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 text-amber-300/70" data-testid="admin-sidebar-section-label">
        Operations
      </div>
      {SECTIONS.map((s) => (
        <NavLink
          key={s.key}
          to={`/admin/${s.path}`}
          end={s.path === ""}
          className={({ isActive }) =>
            `vlk-portal-nav-link ${isActive ? "active" : ""}`
          }
          data-testid={`admin-nav-${s.key}`}
        >
          {s.label}
        </NavLink>
      ))}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────
// All admin pages are now live — placeholders removed.
// ─────────────────────────────────────────────────────────────

